import {
  ConsoleSqlOutlined,
  DeleteOutlined,
  DownOutlined,
  HistoryOutlined,
  SaveOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { CONSTANT } from '@salescore/client-base'
import { canViewRecords, useCanForView, useMe } from '@salescore/client-common'
import { type CompiledFormViewConfig, CORE_CONSTANT, type EltChangeResult, type ViewQueryList } from '@salescore/core'
import { ButtonWithTooltip } from '@salescore/frontend-common'
import { App, Avatar, Button, Card, Col, Dropdown, Empty, Form, message, Popconfirm, Row, Skeleton, Space } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { useRecordsImpl } from '../../recoil/records'
import { useLoadingState } from '../../recoil/records/hooks'
import { useRecordsAndChangesMutation } from '../../recoil/records/mutations/recordsAndChangesMutation'
import {
  type RecordNodePath,
  useSetUpsertViewRecordsByPath,
} from '../../recoil/records/mutations/upsertViewRecordsByPathMutation'
import { changesAtom } from '../../recoil/recordsHistory'
import type { SalescoreObjectAbility, ViewAbility } from '../../recoil/view/atoms'
import { ViewUIRecordNodeContext, ViewUIRecordNodeContextClass } from './contexts'
import { ModelIcon } from './ModelIcon'
import { buildViewQueryResultsMetadataModalProperties } from './ViewQueryResultsMetadataModal'
import { ViewUIContext, ViewUIFormComponentC } from './ViewUIComponent'
import { buildChangesModalProperties } from './ViewUISheetWithNavigation/SheetNavigation/ChangesModal'
import { buildViewDebugModalProperties } from './ViewUISheetWithNavigation/ViewDebugModal'

interface ViewQueryRecordNodeForForm {
  id: string | undefined
  attributes: Record<string, unknown>
  meta: {
    height: number
    innerRowIndexStart: number
    innerRowIndexEnd: number
    // eslint-disable-next-line sonarjs/todo-tag
    highlightAttributeNames?: string[] // TODO: 後から適切な形式を考える予定
  }
  children: Record<string, ViewQueryRecordNodeForForm[]>
}

export const ViewUIFormC = ({
  compiledConfig,
  view,
  ability,
}: {
  compiledConfig: CompiledFormViewConfig
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
}): ReactNode => {
  const [component] = compiledConfig.ui
  const me = useMe()
  const { modal } = App.useApp()
  const changes = useRecoilValue(changesAtom)
  const upsertViewRecordsByPath = useSetUpsertViewRecordsByPath(compiledConfig, ability)
  const loading = useLoadingState()
  const records = useRecordsImpl()
  const { onFinish } = useContext(ViewUIContext)
  const [form] = useForm()
  const reference = useRef<HTMLDivElement>(null)
  const query = compiledConfig.queries[0]
  const recordsAndChangesMutation = useRecordsAndChangesMutation(compiledConfig, view, query)
  // eslint-disable-next-line sonarjs/no-unused-vars
  const [_, setRecordSaveErrors] = useState<EltChangeResult[]>([])

  const [isSaving, setIsSaving] = useState(false)
  const [isDeleteClicked, setIsDeleteClicked] = useState(false) // こうしないとrecoilの反映がされないことがあるため
  const [isSaveClicked, setIsSaveClicked] = useState(false) // こうしないとViewUIInputの反映がされないことがあるため
  const canSaveRecord = useCanForView('save-record', view)
  const canDeleteRecord = useCanForView('delete-record', view)
  const [validationErrorVisibility, setValidationErrorVisibility] = useState(false) // AntD の Form.Item のエラーが使えない構成になっているため、仕方なく状態を定義している: https://gitlab.com/salescore-inc/dev/salescore/-/merge_requests/4774#note_2642493042

  const onSave = async () => {
    setIsSaving(true)

    try {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: エラーの処理どうするんだっけ？
      // 現状では catch 節で捕まえたエラーのメッセージを出しているだけ
      const successes = await recordsAndChangesMutation.onSave(setRecordSaveErrors)
      setValidationErrorVisibility(false)
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO:
      // void message.success(`${records.length}件のレコードを保存しました`)
      if (onFinish !== undefined) {
        onFinish({ id: successes?.[0]?.result.id })
      }
    } catch (error) {
      setValidationErrorVisibility(true)
      if (error instanceof Error) {
        void message.error(error.message)
      } else {
        void message.error(t(`エラーが発生しました`))
      }
    } finally {
      setIsSaving(false)
      setIsSaveClicked(false)
    }
  }

  const onDelete = async () => {
    try {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: エラーの処理どうするんだっけ？
      // 現状では catch 節で捕まえたエラーのメッセージを出しているだけ
      await recordsAndChangesMutation.onSave(setRecordSaveErrors)
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO:
      // void message.success(`${records.length}件のレコードを保存しました`)
      if (onFinish !== undefined) {
        onFinish({ id: records[0]?.id })
      }
    } catch (error) {
      if (error instanceof Error) {
        void message.error(error.message)
      } else {
        void message.error(t(`エラーが発生しました`))
      }
    } finally {
      setIsSaveClicked(false)
      setIsDeleteClicked(false)
    }
  }

  const onConfirmDelete = (isDeleteAllChildren?: boolean): void => {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: 途中で更新してから削除した場合どうする？
    upsertViewRecordsByPath({
      deleteRecordChanges: [
        {
          recordNodePath,
          isDeleteAllChildren,
        },
      ],
    })
    // onDeleteをここで呼ぶと、上記の変更がrecoilに反映される前にsaveされてしまう
    setIsDeleteClicked(true)
  }

  useEffect(() => {
    if (isSaveClicked) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void onSave()
    }
  }, [isSaveClicked])

  useEffect(() => {
    if (isDeleteClicked) {
      void onDelete()
    }
  }, [isDeleteClicked])

  useEffect(() => {
    if (reference.current !== null) {
      reference.current.focus()
    }
  }, [])

  if (loading.isTrue) {
    return <Skeleton active />
  }

  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: firstをどこでやるか？
  if (!Array.isArray(records) || records.length === 0) {
    return <Empty />
  }
  const [recordNode] = records

  if (recordNode === undefined) {
    return <Empty />
  }

  const isNewRecord = recordNode.id.startsWith(CORE_CONSTANT.VIEW_NEW_RECORD_PREFIX)
  const nameValue =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    (recordNode.attributes[component.nameFieldName ?? ''] as string | undefined) ??
    // eslint-disable-next-line sonarjs/todo-tag
    (isNewRecord ? '新規レコード' : undefined) // TODO
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,sonarjs/todo-tag
  const urlValue = recordNode.attributes[component.recordUrlFieldName ?? ''] as string | undefined // TODO
  const recordNodePath: RecordNodePath = []
  const relatedBlockComponents = component.children.slice(1) // XXX: 右の「関連」に出すコンポーネント。本当はこんなロジックは書きたくないが、現状はこれが一番実装が楽なのでこの形になっている

  return (
    <div
      ref={reference}
      // onKeyDown={(event) => {
      //   const isWindows = platform.os?.family === 'Windows'
      //   const commandKey: boolean = isWindows ? event.ctrlKey : event.metaKey

      //   switch (event.key) {
      //     case 'Enter': {
      //       if (commandKey) {
      //         form.submit()
      //         event.preventDefault()
      //       }
      //       return
      //     }
      //     case 's': {
      //       if (commandKey) {
      //         form.submit()
      //         event.preventDefault()
      //       }
      //       return
      //     }
      //   }

      //   handleKeyDown(event)
      // }}
    >
      <PageHeader
        title={
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: 雑に調整している
          <Space style={{ marginTop: -12 }}>
            {/* <BankOutlined/> */}
            <div className="mt-3">
              <Avatar
                style={{ backgroundColor: component.color ?? '#888' }}
                size={45}
                shape={'square'}
                icon={<ModelIcon icon={component.icon} fontSize={26} />}
              />
            </div>
            <div className="" style={{ lineHeight: 1 }}>
              <span className="text-sm font-normal text-gray-600">{component.label}</span>
              <br />
              {urlValue === undefined ? (
                <span>{nameValue ?? recordNode.id}</span>
              ) : (
                <a target="_blank" href={urlValue} rel="noreferrer">
                  {nameValue ?? recordNode.id}
                </a>
              )}
            </div>
          </Space>
        }
        extra={[
          canViewRecords(me) ? (
            // eslint-disable-next-line @eslint-react/no-missing-key
            <Dropdown
              menu={{
                items: [
                  {
                    key: `record`,
                    label: (
                      <Space className={CONSTANT.colorClasses.ADMIN_TEXT_COLOR}>
                        <ConsoleSqlOutlined />
                        {t(`レコードを表示`)}
                      </Space>
                    ),
                    onClick: () => {
                      const m = modal.confirm(
                        buildViewDebugModalProperties({
                          content: {
                            records,
                          },
                          onChange: () => {
                            m.destroy()
                          },
                          onAfterFinish: () => {
                            m.destroy()
                          },
                        }),
                      )
                    },
                  },
                ],
              }}
            >
              <Button icon={<SettingOutlined />} />
            </Dropdown>
          ) : (
            // eslint-disable-next-line @eslint-react/no-missing-key
            <></>
          ),
          isNewRecord ? (
            // eslint-disable-next-line @eslint-react/no-missing-key
            <></>
          ) : (
            // eslint-disable-next-line @eslint-react/no-missing-key
            <Button
              onClick={() => {
                modal.confirm(buildViewQueryResultsMetadataModalProperties(compiledConfig, view, query))
              }}
              icon={<ConsoleSqlOutlined />}
            />
          ),
          <Button
            key="changes"
            disabled={changes.length === 0}
            onClick={() => {
              modal.confirm(buildChangesModalProperties(compiledConfig, view, query))
            }}
            icon={<HistoryOutlined />}
          />,
          isNewRecord ? (
            // eslint-disable-next-line @eslint-react/no-missing-key
            <></>
          ) : // eslint-disable-next-line sonarjs/no-nested-conditional
          canDeleteRecord ? (
            // eslint-disable-next-line @eslint-react/no-missing-key
            <DropdownDeleteButton
              onDelete={() => {
                onConfirmDelete()
              }}
              onDeleteWithAllChildren={() => {
                onConfirmDelete(true)
              }}
              query={query}
            />
          ) : (
            // eslint-disable-next-line @eslint-react/no-missing-key
            <ButtonWithTooltip danger disabled tooltipTitle={t(`権限がありません`)} icon={<DeleteOutlined />}>
              {t(`削除`)}
            </ButtonWithTooltip>
          ),
          <ButtonWithTooltip
            key="save"
            showTooltip={!canSaveRecord}
            disabled={!canSaveRecord || changes.length === 0}
            tooltipTitle={t(`権限がありません`)}
            icon={<SaveOutlined />}
            type="primary"
            htmlType="submit"
            loading={isSaving}
            onClick={() => {
              setIsSaveClicked(true)
            }}
          >
            {t(`保存`)}
          </ButtonWithTooltip>,
        ]}
      >
        <ViewUIRecordNodeContext.Provider
          value={
            new ViewUIRecordNodeContextClass({
              recordNode,
              recordNodePath,
              upsertViewRecordsByPath,
            })
          }
        >
          <Form<ViewQueryRecordNodeForForm>
            form={form}
            layout="vertical"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
            onFinish={onSave}
          >
            {/* この辺のレイアウト設定はconfig側で正しくやりたいが、一旦現状のユースケースのみで気軽に設定したいので、決め打ちで以下を表示 */}
            <Row gutter={16}>
              {relatedBlockComponents.length > 0 ? (
                <>
                  <Col span={14}>
                    <Card title={t(`詳細`)}>
                      <ViewUIFormComponentC
                        component={
                          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          component.children[0]!
                        }
                        compiledConfig={compiledConfig}
                        showError={validationErrorVisibility}
                        view={view}
                        ability={ability}
                      />
                    </Card>
                  </Col>
                  <Col span={10}>
                    <Card title={t(`関連`)} styles={{ body: { padding: 8 } }}>
                      {relatedBlockComponents.map((child, index) => (
                        <ViewUIFormComponentC
                          key={`viewuiformcomponentc-related-${index}`}
                          component={child}
                          compiledConfig={compiledConfig}
                          showError={validationErrorVisibility}
                          view={view}
                          ability={ability}
                        />
                      ))}
                      {component.children.length <= 1 && <div>{t(`関連なし`)}</div>}
                    </Card>
                  </Col>
                </>
              ) : (
                <Col span={24}>
                  <Card>
                    <ViewUIFormComponentC
                      component={
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        component.children[0]!
                      }
                      compiledConfig={compiledConfig}
                      showError={validationErrorVisibility}
                      view={view}
                      ability={ability}
                    />
                  </Card>
                </Col>
              )}
            </Row>
          </Form>
        </ViewUIRecordNodeContext.Provider>
      </PageHeader>
    </div>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function DropdownDeleteButton({
  onDelete,
  onDeleteWithAllChildren,
  query,
}: {
  onDelete: () => void
  onDeleteWithAllChildren: () => void
  query: ViewQueryList
}): ReactNode {
  const [open, setOpen] = useState(false)

  return (
    <Dropdown
      menu={{
        items: [
          {
            key: '1',
            label: (
              <DeleteButtonMenu
                label={t(`レコードの削除`)}
                onConfirm={() => {
                  onDelete()
                }}
              />
            ),
          },
          (query.tree.children ?? []).length > 0
            ? {
                key: '2',
                label: (
                  <DeleteButtonMenu
                    label={t(`レコードの削除(子孫含む)`)}
                    onConfirm={() => {
                      onDeleteWithAllChildren()
                    }}
                  />
                ),
              }
            : undefined,
        ].filter(isNotNullish),
      }}
      open={open}
      trigger={['click']}
      onOpenChange={(open, info) => {
        if (info.source === 'trigger' || open) {
          setOpen(open)
        }
      }}
    >
      <Button type="primary" shape="round" danger>
        <div className="flex items-center gap-2">
          <DeleteOutlined />
          <DownOutlined style={{ fontSize: 10 }} />
        </div>
      </Button>
    </Dropdown>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function DeleteButtonMenu({ label, onConfirm }: { label: string; onConfirm: () => void }): ReactNode {
  return (
    <Popconfirm
      title={
        <div>
          {t(`本当に削除しますか？(この操作は取り消せません。`)}
          <br />
          {t(`また、変更中の項目はすべてクリアされます)`)}
        </div>
      }
      onConfirm={onConfirm}
      okText={t(`削除`)}
      cancelText={t(`キャンセル`)}
    >
      <span className="inline-block w-full text-red-500">{label}</span>
    </Popconfirm>
  )
}
